import { AlertTriangle, ExternalLink, X } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";

/**
 * PortalWarningBanner - Displays a prominent warning for jurisdictions
 * that require mandatory online portal submission (e.g., Green Halo, EZOP)
 */
const PortalWarningBanner = ({ 
  jurisdiction, 
  onProceed, 
  onDismiss,
  showUpgrade = true 
}) => {
  if (!jurisdiction?.portal_required?.mandatory) {
    return null;
  }

  const portal = jurisdiction.portal_required;
  const portalName = portal.name || "Online Portal";
  const portalUrl = portal.url || "#";
  const isHybrid = portal.hybrid || false;
  const contact = jurisdiction.contact || {};
  const warningText = jurisdiction.warning_text || "";

  return (
    <Card className="border-2 border-red-500/50 bg-red-500/10 mb-6 overflow-hidden" data-testid="portal-warning-banner">
      {/* Header */}
      <div className="bg-red-500/20 px-4 py-3 border-b border-red-500/30 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-red-500 animate-pulse" />
          <span className="font-bold text-red-500 text-lg">MANDATORY PORTAL SUBMISSION</span>
        </div>
        {onDismiss && (
          <button 
            onClick={onDismiss}
            className="text-red-500/70 hover:text-red-500"
            data-testid="dismiss-warning"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <CardContent className="p-4 space-y-4">
        {/* Main Warning */}
        <div className="space-y-2">
          <p className="text-foreground font-medium">
            This jurisdiction requires official submission through{" "}
            <span className="text-red-400 font-bold">{portalName}</span>.
          </p>
          
          {warningText && (
            <p className="text-sm text-muted-foreground">
              {warningText}
            </p>
          )}
        </div>

        {/* Portal Info Box */}
        <div className="bg-card/50 border border-primary/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="font-semibold text-foreground">Official Portal</span>
            <Badge variant="outline" className={isHybrid ? "border-yellow-500 text-yellow-500" : "border-red-500 text-red-500"}>
              {isHybrid ? "Hybrid Export OK" : "Portal Only"}
            </Badge>
          </div>
          
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Portal:</span>
              <a 
                href={portalUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline flex items-center gap-1"
                data-testid="portal-link"
              >
                {portalName} <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            
            {contact.phone && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Phone:</span>
                <span className="text-foreground">{contact.phone}</span>
              </div>
            )}
            
            {contact.email && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Email:</span>
                <a href={`mailto:${contact.email}`} className="text-primary hover:underline">
                  {contact.email}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* What this means */}
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
          <p className="text-sm text-yellow-200 font-medium mb-2">What does this mean?</p>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>• This tool will generate a <strong className="text-foreground">draft/reference PDF</strong></li>
            {isHybrid ? (
              <>
                <li>• You may <strong className="text-foreground">attach this draft</strong> to your portal submission</li>
                <li>• Official submission must still go through {portalName}</li>
              </>
            ) : (
              <>
                <li>• This PDF will <strong className="text-red-400">NOT be accepted</strong> as a standalone submission</li>
                <li>• Use this PDF for <strong className="text-foreground">planning purposes only</strong></li>
              </>
            )}
            <li>• Visit the portal link above to submit officially</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button
            onClick={onProceed}
            variant="outline"
            className="flex-1 border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10"
            data-testid="proceed-draft-btn"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Generate Draft Anyway
          </Button>
          
          <Button
            asChild
            className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <a href={portalUrl} target="_blank" rel="noopener noreferrer" data-testid="go-to-portal-btn">
              Go to {portalName} <ExternalLink className="w-4 h-4 ml-2" />
            </a>
          </Button>
        </div>

        {/* Premium Upsell */}
        {showUpgrade && (
          <div className="border-t border-primary/20 pt-4 mt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Need portal-compatible formatting?</p>
                <p className="text-xs text-muted-foreground">
                  Premium tier includes portal guidance, tracking tips, and optimized exports.
                </p>
              </div>
              <Badge className="bg-primary/20 text-primary border-primary/30">
                Upgrade
              </Badge>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PortalWarningBanner;
